import api from "../../../api"; // shared instance, token refresh handle qiladi
import type {
  PaginatedResponse,
  UserItem,
  TopicItem,
  DashStats,
  DayStats,
  AdminTicketListItem,
  AdminTicketQuestionsResponse,
  AdminQuestionResponse,
  NotificationItem,
  VideoItem,
  QuestionReportItem,
  QuestionReportStatus,
} from "./adminTypes";
import { QuestionType } from "./adminTypes";

type Lang = "oz" | "uz" | "ru";

export const adminAPI = {
  getTicketsList: () => api.get<AdminTicketListItem[]>("/admin/tickets/list"),

  getTicketQuestions: (ticketId: number) =>
    api.get<AdminTicketQuestionsResponse>(
      `/admin/tickets/${ticketId}/questions`,
    ),
  // ─── STATS ───────────────────────────────────────────────────────
  getStats: () => api.get<DashStats>("/admin/stats"),

  getUserGrowth: (period: "week" | "month" | "year") =>
    api.get<DayStats[]>(`/admin/stats/growth?period=${period}`),

  // ─── USERS ───────────────────────────────────────────────────────
  getUsers: (
    page = 1,
    limit = 10,
    search = "",
    authProvider: "" | "local" | "google" | "telegram" = "",
  ) => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", String(limit));
    if (search.trim()) params.set("search", search.trim());
    if (authProvider) params.set("auth_provider", authProvider);
    return api.get<PaginatedResponse<UserItem>>(`/users?${params.toString()}`);
  },
  createQuestion: (data: {
    question_oz: string;
    question_uz: string;
    question_ru: string;
    correct_answer_index: number;
    explanation_oz?: string;
    explanation_uz?: string;
    explanation_ru?: string;
    topicId?: number;
    question_type?: QuestionType;
    keywords?: string[];
    choices: {
      order_index: number;
      text_oz: string;
      text_uz: string;
      text_ru: string;
    }[];
  }) =>
    api.post<{ message: string; questionId: number }>(
      "/admin/questions/create",
      data,
    ),
  deleteUser: (id: string) => api.delete(`/users/${id}`),

  updateUser: (id: string, data: Partial<UserItem>) =>
    api.patch(`/users/${id}`, data),

  // ─── QUESTIONS ───────────────────────────────────────────────────
  getQuestions: (page = 1, limit = 20, search = "", type = "") => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", String(limit));
    if (search.trim()) params.set("search", search.trim());
    if (type) params.set("type", type);
    return api.get<PaginatedResponse<AdminQuestionResponse>>(
      `/admin/questions?${params.toString()}`,
    );
  },

  getQuestion: (id: number) =>
    api.get<AdminQuestionResponse>(`/admin/questions/${id}`),

  updateQuestion: (id: number, data: any) =>
    api.put(`/admin/questions/${id}`, data),

  // adminAPI.ts ichiga qo'shing
  getTopicsForSelect: (search: string) => {
    return api.get<
      { id: number; name_uz: string; name_oz: string; name_ru: string }[]
    >(`/admin/topics/select?search=${search}`);
  },

  uploadQuestionPhoto: (questionId: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post<{ photo_url: string }>(
      `/admin/questions/${questionId}/upload-photo`,
      formData,
    );
  },

  uploadQuestionAudio: (questionId: number, lang: Lang, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post<{ audio_url: string; lang: Lang }>(
      `/admin/questions/${questionId}/upload-audio?lang=${lang}`,
      formData,
    );
  },

  deleteQuestion: (id: number) => api.delete(`/admin/questions/${id}`),

  // ─── TOPICS ──────────────────────────────────────────────────────
  getTopics: (page = 1, limit = 20, search = "") => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", String(limit));
    if (search.trim()) params.set("search", search.trim());
    return api.get<PaginatedResponse<TopicItem>>(
      `/admin/topics?${params.toString()}`,
    );
  },

  createTopic: (
    data: Omit<TopicItem, "id" | "created_at" | "question_count">,
  ) => api.post<TopicItem>("/admin/topics", data),

  updateTopic: (id: number, data: Partial<TopicItem>) =>
    api.patch(`/admin/topics/${id}`, data),

  deleteTopic: (id: number) => api.delete(`/admin/topics/${id}`),

  // ─── NOTIFICATIONS ───────────────────────────────────────────────
  getNotifications: () => api.get<NotificationItem[]>("/notifications"),

  createNotification: (data: { title: string; text: string; image?: File }) => {
    const formData = new FormData();
    formData.append("title", data.title);
    formData.append("text", data.text);
    if (data.image) formData.append("image", data.image);
    return api.post<NotificationItem>("/notifications", formData);
  },

  updateNotification: (
    id: number,
    data: { title?: string; text?: string; image?: File },
  ) => {
    const formData = new FormData();
    if (data.title !== undefined) formData.append("title", data.title);
    if (data.text !== undefined) formData.append("text", data.text);
    if (data.image) formData.append("image", data.image);
    return api.put<NotificationItem>(`/notifications/${id}`, formData);
  },

  deleteNotification: (id: number) => api.delete(`/notifications/${id}`),

  // ─── VIDEOS ──────────────────────────────────────────────────────
  getVideos: () => api.get<VideoItem[]>("/videos"),

  createVideo: (data: {
    title: string;
    description?: string;
    video_url?: string;
    order_index?: number;
  }) => api.post<VideoItem>("/videos", data),

  updateVideo: (id: number, data: Partial<VideoItem>) =>
    api.put<VideoItem>(`/videos/${id}`, data),

  uploadVideoFile: (id: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.put<VideoItem>(`/videos/${id}/upload-video`, formData);
  },

  uploadVideoThumbnail: (id: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.put<VideoItem>(`/videos/${id}/upload-thumbnail`, formData);
  },

  deleteVideo: (id: number) => api.delete(`/videos/${id}`),

  // ─── QUESTION REPORTS ────────────────────────────────────────────
  getReports: (status?: QuestionReportStatus, questionId?: number) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (questionId) params.set("questionId", String(questionId));
    const qs = params.toString();
    return api.get<QuestionReportItem[]>(
      `/question-reports${qs ? `?${qs}` : ""}`,
    );
  },

  updateReportStatus: (id: number, status: QuestionReportStatus) =>
    api.patch<QuestionReportItem>(`/question-reports/${id}/status`, {
      status,
    }),
};
